import React, { Component } from "react";
import { Link, Redirect } from "react-router-dom";
import { AxiosResponse } from "axios";
import ReactHtmlParser from "react-html-parser";
import Discussion from "./Discussion";
import Conversations from "./Conversations";
import Assignment from "./Assignment";
import { QuestionPreview } from "../../../stores/CoursePreviewStore/Types";
import { Answer, Enrolment } from "../../../stores/EnrolmentStore/Types";
import { getRequest, postRequest } from "../../../utils/httpRequest";
import { errorNotification } from "../../../utils/helpers";
import { CommentResource } from "../../../stores/CommentStore/Types";
import PersonalLessonSummary from "./PersonalLessonSummary";
import { nextLessonPath } from "../helpers";
import QuestionAudioPlayer from "./QuestionAudioPlayer";
import {
  allowsLessonAudio,
  isInteractiveCourse,
  isUnstartedInteractiveAnswer,
} from "../../../utils/interactiveCourse";
import AnswerUpvotes from "./AnswerUpvotes";

interface Props {
  enrolment: Enrolment;
  lesson: QuestionPreview;
  enrolmentId: number;
  lessonId: number;
  answer: Answer;
  page: string;
  refreshAnswers: () => void;
  loadingLesson: boolean;
  loadingAnswer: boolean;
  lastLesson: QuestionPreview;
  getXp: () => void;
  getRecentNotifications: () => void;
  currentUser: any;
  answerId?: number;
  getAnswer?: () => void;
  search?: string;
}

interface State {
  comments: CommentResource[];
  loaded: boolean;
  usingLive: boolean;
  showPersonalizedContent: boolean;
}

class Landing extends Component<Props, State> {
  observer = null;
  constructor(props) {
    super(props);

    this.state = {
      comments: [],
      loaded: false,
      usingLive: false,
      showPersonalizedContent: props.lesson?.show_personalized_content ?? true,
    };
  }

  fetchComments = () => {
    return getRequest(`/api/comments.json?question_id=${this.props.lessonId}`)
      .then((response: AxiosResponse) => {
        this.setState({ comments: response.data, loaded: true });
      })
      .catch((error) => {
        this.setState({ loaded: true });

        errorNotification(
          "Error loading comments",
          JSON.stringify(error.response.data),
        );
      });
  };

  updateComments = () => {
    this.setState({ loaded: false });
    this.fetchComments();
  };

  componentDidMount() {
    if (this.props.page !== "answerUpvotes") {
      this.scrollToTop();
    }
    this.fetchComments();
    this.appendUserIdToUrls(); // Update existing links

    // Create a MutationObserver to watch for added nodes
    this.observer = new MutationObserver(() => {
      this.appendUserIdToUrls();
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true, // Watch for changes inside the entire document
    });
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.lessonId !== this.props.lessonId) {
      this.updateComments();
      // Update showPersonalizedContent from new lesson data
      if (this.props.lesson?.show_personalized_content !== undefined) {
        this.setState({
          showPersonalizedContent: this.props.lesson.show_personalized_content,
        });
      }
    }
  }

  scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  appendUserIdToUrls = () => {
    const userId = this.props?.currentUser?.id;
    const links = document.querySelectorAll(".append-user-id");

    links.forEach((link) => {
      if ((link as any).href && !(link as any).href.includes("user_id=")) {
        const url = new URL((link as any).href);
        url.searchParams.set("user_id", userId);
        url.searchParams.set("ref", userId);
        (link as any).href = url.toString();
      }
    });
  };

  useLive = (e) => {
    e.preventDefault();

    const { answer } = this.props;

    this.setState({ usingLive: true });

    postRequest(`/api/answers/${answer.id}/life_uses`, {})
      .then((response: AxiosResponse) => {
        window.location.reload();
      })
      .catch((error) => {
        this.setState({ usingLive: false });

        errorNotification(
          "There was an error with using lives",
          JSON.stringify(error.response.data),
        );
      });
  };

  togglePersonalizedContent = () => {
    const { enrolmentId } = this.props;
    const newValue = !this.state.showPersonalizedContent;

    this.setState({ showPersonalizedContent: newValue });

    postRequest(
      `/api/enrolments/${enrolmentId}/personalization/toggle_content`,
      { show_personalized_content: newValue },
    ).catch((error) => {
      // Revert on error
      this.setState({ showPersonalizedContent: !newValue });
      errorNotification(
        "Error toggling content preference",
        JSON.stringify(error?.response?.data),
      );
    });
  };

  nextQuestionPath = () => {
    const { lesson, lastLesson, enrolment } = this.props;

    return nextLessonPath(lesson, lastLesson, enrolment);
  };

  renderHero = (translation) => {
    const { lesson } = this.props;
    const heroImage = lesson?.topic?.masthead_image_url;
    const heroTitle = translation ? translation.name : lesson?.name;
    const chapterNumber = (lesson?.topic?.position || 0) + 1;
    const lessonNumber = (lesson?.position || 0) + 1;

    return (
      <section
        className="l-hero"
        aria-labelledby="lessonTitle"
        key={this.props.currentUser?.locale}
      >
        <div
          className="l-hero__bg"
          aria-hidden="true"
          style={
            heroImage
              ? {
                  backgroundImage: `linear-gradient(180deg, rgba(91, 74, 99, 0.82) 0%, rgba(74, 60, 84, 0.9) 100%), url(${heroImage})`,
                  backgroundPosition: "center",
                  backgroundSize: "cover",
                }
              : undefined
          }
        />
        <div className="l-hero__inner">
          <p className="l-hero__eyebrow">
            Chapter {chapterNumber} &nbsp;|&nbsp; Lesson {lessonNumber}
          </p>
          <h1 className="l-hero__title" id="lessonTitle">
            {heroTitle}
          </h1>
          <p className="l-hero__meta">
            <svg aria-hidden="true">
              <use href="#i-clock" />
            </svg>
            {lesson?.length_minutes} min
          </p>
        </div>
      </section>
    );
  };

  // Interactive courses run in order: until this lesson's challenge is actually
  // submitted, Next must not offer a way past it. isUnstartedInteractiveAnswer
  // covers the seeded-but-untouched answer these courses create up front.
  nextLessonLocked = () => {
    const { enrolment, lesson, answer } = this.props;

    if (!isInteractiveCourse(enrolment)) return false;

    return !answer || isUnstartedInteractiveAnswer(lesson, answer);
  };

  render() {
    const {
      enrolment,
      enrolmentId,
      lesson,
      lessonId,
      answer,
      refreshAnswers,
      loadingLesson,
      loadingAnswer,
      lastLesson,
      getXp,
      page,
      getRecentNotifications,
      currentUser,
      answerId,
      getAnswer,
      search,
    } = this.props;

    const { comments, loaded, usingLive, showPersonalizedContent } = this.state;
    const translation = lesson?.translations?.find(
      (t) => t.language === currentUser?.locale,
    );

    const audioLanguage = currentUser?.locale || "en";
    const audio = lesson?.audio_files?.find(
      (t) => t.language === audioLanguage,
    );

    const hasPersonalizedContent = !!lesson?.formatted_personal_lesson_content;
    const displayPersonalized =
      hasPersonalizedContent && showPersonalizedContent;
    const isGenerating = ["generating", "generating_progress"].includes(
      enrolment?.personalization_state,
    );
    const showGeneratingIndicator = isGenerating && !hasPersonalizedContent;

    const contentToDisplay = displayPersonalized
      ? lesson?.formatted_personal_lesson_content
      : translation
        ? translation.formatted_foreword
        : lesson?.formatted_foreword;

    return (
      <>
        {loadingLesson ? (
          <>
            <div className="ui active inverted dimmer">
              <div className="ui text loader">Loading</div>
            </div>
            {this.renderHero(translation)}
          </>
        ) : (
          <>
            {page !== "answerUpvotes" &&
              lesson?.kind === "Opinion" &&
              answer &&
              answer.state == "pending" && (
                <Redirect
                  to={`/enrolments/${enrolment.id}/questions/${lessonId}/answers/${answer.id}/votes`}
                />
              )}
            {this.renderHero(translation)}
            <div className="l-wrap">
              <article className="l-card">
                <div className="l-inner">
                  {((audio && allowsLessonAudio(enrolment)) ||
                    hasPersonalizedContent) && (
                    <div className="l-toolbar">
                      {audio && allowsLessonAudio(enrolment) && (
                        <div className="tb-field audio">
                          <div className="tb-label">Listen to this lesson</div>
                          <QuestionAudioPlayer audioUrl={audio?.audio_url} />
                        </div>
                      )}

                      {hasPersonalizedContent && (
                        <div className="tb-field tb-field--seg">
                          <div className="tb-label">Choose your version</div>
                          <div
                            className="seg"
                            role="group"
                            aria-label="Lesson version"
                          >
                            <button
                              type="button"
                              aria-pressed={!displayPersonalized}
                              onClick={
                                displayPersonalized
                                  ? this.togglePersonalizedContent
                                  : undefined
                              }
                            >
                              Standard
                            </button>
                            <button
                              type="button"
                              className="is-ai"
                              aria-pressed={displayPersonalized}
                              onClick={
                                !displayPersonalized
                                  ? this.togglePersonalizedContent
                                  : undefined
                              }
                            >
                              <svg className="spark" aria-hidden="true">
                                <use href="#i-spark" />
                              </svg>
                              Personalised
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="lesson-text-target prose">
                    {answer &&
                      ["pending", "submitted"].includes(answer.state) && (
                        <div className="status-container pending-message">
                          <h4>Hold tight, we’re marking this lesson for you</h4>
                        </div>
                      )}

                    {answer && answer.state === "passed" && (
                      <div className="status-container passed-message">
                        <h4>Bravo! You’ve passed this lesson</h4>
                      </div>
                    )}
                    {answer &&
                      answer.state === "failed" &&
                      enrolment.lives_count > 0 && (
                        <div className="status-container failed-message">
                          <h4>Whoopsie daisy</h4>
                          <button
                            className={`ui button ${
                              usingLive ? "loading" : ""
                            }`}
                            onClick={this.useLive}
                          >
                            Use 1 life to try again
                          </button>
                        </div>
                      )}

                    <div className="lesson-target" />
                    {showGeneratingIndicator && (
                      <div className="personalized-content-toggle">
                        <span className="generating-indicator">
                          Personalising content...
                        </span>
                      </div>
                    )}
                    <div
                      dangerouslySetInnerHTML={{
                        __html: contentToDisplay,
                      }}
                    />
                    <PersonalLessonSummary
                      enrolment={enrolment}
                      question={lesson}
                    />
                  </div>
                </div>
              </article>
            </div>

            <div className="l-wrap">
              <section className="q-panel" aria-labelledby="qTitle">
                <div className="l-inner">
                  {page === "answerUpvotes" ? (
                    <AnswerUpvotes
                      embedded
                      lesson={lesson}
                      lessonId={lessonId}
                      enrolment={enrolment}
                      answer={answer}
                      lastLesson={lastLesson}
                      answerId={answerId}
                      enrolmentId={enrolmentId}
                      refreshAnswers={refreshAnswers}
                      loadingLesson={loadingLesson}
                      loadingAnswer={loadingAnswer}
                      getRecentNotifications={getRecentNotifications}
                      getAnswer={getAnswer}
                      getXp={getXp}
                      page={page}
                      currentUser={currentUser}
                      search={search}
                    />
                  ) : lesson ? (
                    <Assignment
                      mode={lesson.kind}
                      enrolment={enrolment}
                      question={lesson}
                      lastLesson={lastLesson}
                      answer={answer}
                      loadingAnswer={loadingAnswer}
                      refreshAnswers={refreshAnswers}
                      getRecentNotifications={getRecentNotifications}
                      getXp={getXp}
                      page={page}
                      currentUser={currentUser}
                    />
                  ) : null}
                </div>
              </section>
            </div>

            <div className="l-wrap">
              <nav className="l-nav" aria-label="Lesson navigation">
                <Link
                  className="btn btn-outline btn--compact"
                  to={lesson?.previous_question_path}
                  onClick={this.scrollToTop}
                >
                  <svg aria-hidden="true">
                    <use href="#i-chev-left" />
                  </svg>
                  Prev
                </Link>
                {this.nextLessonLocked() ? (
                  <span
                    className="btn btn-blue btn--compact next-locked"
                    title="Finish this lesson first"
                    aria-label="Finish this lesson first"
                  >
                    Next
                    <svg aria-hidden="true">
                      <use href="#i-lock" />
                    </svg>
                  </span>
                ) : (
                  <Link
                    className="btn btn-blue btn--compact"
                    to={this.nextQuestionPath()}
                    onClick={this.scrollToTop}
                  >
                    Next
                    <svg aria-hidden="true">
                      <use href="#i-chev-right" />
                    </svg>
                  </Link>
                )}
              </nav>
            </div>

            <div className="l-wrap lesson-discussion">
              <Discussion
                updateComments={this.updateComments}
                lessonId={lessonId}
                currentUser={currentUser}
              />
              <Conversations
                comments={comments}
                loaded={loaded}
                questionId={lessonId}
                currentUser={currentUser}
                enrolmentId={enrolmentId}
                updateComments={this.updateComments}
              />
            </div>
          </>
        )}
      </>
    );
  }
}

export default Landing;
